import { useQuery } from '@tanstack/react-query';
import { memo, useMemo } from 'react';

import LiveComponent from './LiveComponent';
import NotLiveComponent from './NotLiveComponent';

import { getMatchAtGlanceByIdQuery } from '@queries/editorial';
import { useMediaAssetService } from '@shared/VideoPlayer/hooks/useMediaAssetService';
import type { Match } from '@shared-types/match.types';
import { MatchStatus } from '@shared-types/match.types';
import { IS_SIMULATION, IS_TEST } from '@utilities/browser';
import { stripHtml } from '@utilities/dom';
import { getRefreshRate } from '@utilities/live';

const Main = ({
  match,
  blogId,
  content,
  hasAudioDescPage,
}: {
  match: Match;
  blogId: string;
  content?: 'LIVE' | 'NON-LIVE';
  hasAudioDescPage?: boolean;
}) => {
  const refreshRate = useMemo(() => getRefreshRate(match), [match]);

  const { data: entity } = useQuery({
    ...getMatchAtGlanceByIdQuery(blogId),
    refetchInterval: !IS_TEST && refreshRate,
  });

  const data = useMemo(() => entity?.matchAtGlanceElements ?? [], [entity?.matchAtGlanceElements]);
  const summary = useMemo(() => entity?.htmlDescription ?? '', [entity?.htmlDescription]);
  const title = useMemo(() => entity?.matchAtGlanceTitle ?? '', [entity?.matchAtGlanceTitle]);

  const videos = data
    .filter((item) => item._attributes?.['emk-class'] === 'video' && item.mainMedia?.hlsStreamUrl)
    .map((item) => item.mainMedia);

  const masResult = useMediaAssetService(videos);

  const filteredData = useMemo(
    () =>
      data.filter(
        (item) =>
          item._attributes?.['emk-class'] !== 'video' ||
          masResult.some((v) => v.id === `${item.mainMedia?.id}` && !!v.hlsStreamUrl),
      ),
    [data, masResult],
  );

  const showLive = useMemo(() => {
    return (
      !!stripHtml(summary) &&
      (match?.status === MatchStatus.LIVE || !filteredData.length || (content && content === 'LIVE') || IS_SIMULATION)
    );
  }, [content, filteredData.length, match?.status, summary]);

  const isLive = match?.status === MatchStatus.LIVE || IS_SIMULATION;

  return (
    <>
      {showLive && <LiveComponent summary={summary} match={match} showAudioDescLink={hasAudioDescPage && isLive} />}
      {!showLive && !!filteredData.length && <NotLiveComponent data={filteredData} title={title} match={match} />}
    </>
  );
};

export default memo(Main);
